import React, { useState, useEffect, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import {
  FiFileText, FiDownload, FiPrinter, FiCalendar,
  FiBarChart2, FiTrendingUp, FiTrendingDown, FiDollarSign,
  FiPercent, FiExternalLink
} from 'react-icons/fi';
import { colors } from '../../theme/colors';
import { formatNumber, formatDate } from '../../utils/formatters';
import useCurrency from '../../hooks/useCurrency';
import useReportMemory from '../../hooks/useReportMemory';
import { collection, getDocs, query, where, orderBy } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useAuth } from '../../context/AuthContext';
import PageLayout from '../../components/PageLayout';
import { navigateToTransaction } from '../../utils/navigationHelper';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line
} from 'recharts';
import { addPrintBrand } from '../../utils/printBranding';
import WhtStatement from './WhtStatement';

// Chart colors
const CHART_COLORS = {
  primary: '#800020',
  secondary: '#A0153E',
  tertiary: '#5D0017',
  accent1: '#C4A484',
  accent2: '#8B7355',
  accent3: '#D4AF37'
};

const PIE_COLORS = [
  CHART_COLORS.primary,
  CHART_COLORS.secondary,
  CHART_COLORS.accent1,
  CHART_COLORS.accent2,
  CHART_COLORS.accent3,
  CHART_COLORS.tertiary,
  '#6A994E',
  '#BC6C25',
  '#606C38',
  '#DDA15E'
];

// System account codes
const SYSTEM_ACCOUNTS = {
  GST_OUTPUT: '21201001',
  GST_INPUT: '21202001',
  WHT_PAYABLE: '21203001'
};

// Local format helpers for print/export
const fmtAmount = (amount) => {
  const dp = (typeof window !== 'undefined' && Number.isFinite(Number(window.__AMAL_DECIMAL_PLACES__)))
    ? Number(window.__AMAL_DECIMAL_PLACES__) : 2;
  if (!amount && amount !== 0) return dp > 0 ? '0.' + '0'.repeat(dp) : '0';
  return Number(amount).toLocaleString('en-PK', {
    minimumFractionDigits: dp,
    maximumFractionDigits: dp
  });
};

const fmtDate = (dateString) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-PK', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
};

// Month names for grouping
const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// Quarter labels
const getQuarterLabel = (month) => {
  if (month <= 2) return 'Q1 (Jul-Sep)';
  if (month <= 5) return 'Q2 (Oct-Dec)';
  if (month <= 8) return 'Q3 (Jan-Mar)';
  return 'Q4 (Apr-Jun)';
};

const getQuarterKey = (dateStr) => {
  const d = new Date(dateStr);
  const m = d.getMonth(); // 0-11
  const y = d.getFullYear();
  const q = Math.floor(m / 3) + 1;
  return `${y}-Q${q}`;
};

const getQuarterDisplayLabel = (key) => {
  // key format: "2024-Q1"
  const parts = key.split('-');
  return `${parts[1]} ${parts[0]}`;
};

const getMonthKey = (dateStr) => {
  const d = new Date(dateStr);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const getMonthDisplayLabel = (key) => {
  const parts = key.split('-');
  const monthIdx = parseInt(parts[1], 10) - 1;
  return `${MONTH_NAMES[monthIdx]} ${parts[0]}`;
};

const TaxReports = () => {
  const { formatCurrency } = useCurrency();
  const { activeCompany, activeFinancialYear, activeBranch } = useAuth();
  // Backward memory: restore filters + generated rows on Back (0 re-reads).
  // savedMem is null on a first visit → defaults apply unchanged.
  const [savedMem, saveMemory] = useReportMemory('tax-reports');
  const taxLabel = activeCompany?.taxLabel || 'GST';
  const navigate = useNavigate();

  // Data state
  const [transactions, setTransactions] = useState(() => savedMem?.transactions ?? []);
  const [financialYears, setFinancialYears] = useState([]);

  // Filter state
  const [selectedFYId, setSelectedFYId] = useState(() => savedMem?.selectedFYId ?? '');
  const [dateFrom, setDateFrom] = useState(() => savedMem?.dateFrom ?? '');
  const [dateTo, setDateTo] = useState(() => savedMem?.dateTo ?? '');
  const [taxType, setTaxType] = useState(() => savedMem?.taxType ?? 'All');
  const [reportType, setReportType] = useState(() => savedMem?.reportType ?? 'Summary');
  const [period, setPeriod] = useState(() => savedMem?.period ?? 'Monthly');

  // UI state
  // Tabs: the existing Tax Summary, and the WHT Statement (Withholding Tax
  // Detail — payments / receipts / advance tax 236H, one row per invoice).
  const [view, setView] = useState(() => savedMem?.view ?? 'summary');
  const [loading, setLoading] = useState(false);
  const [hasGenerated, setHasGenerated] = useState(() => savedMem?.hasGenerated ?? false);

  // ============================================================
  // LOAD FINANCIAL YEARS
  // ============================================================
  const loadFinancialYears = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const fyRef = collection(db, 'companies', activeCompany.id, 'financialYears');
      const q = query(fyRef, orderBy('startDate', 'desc'));
      const snapshot = await getDocs(q);
      const fys = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      setFinancialYears(fys);

      // Only apply the default FY + date range on a fresh visit. On a Back
      // restore (savedMem present) keep the restored filters untouched.
      if (!savedMem) {
        if (activeFinancialYear?.id) {
          setSelectedFYId(activeFinancialYear.id);
          if (activeFinancialYear.startDate) setDateFrom(activeFinancialYear.startDate);
          if (activeFinancialYear.endDate) setDateTo(activeFinancialYear.endDate);
        } else if (fys.length > 0) {
          setSelectedFYId(fys[0].id);
          if (fys[0].startDate) setDateFrom(fys[0].startDate);
          if (fys[0].endDate) setDateTo(fys[0].endDate);
        }
      }
    } catch (error) {
      console.error('Error loading financial years:', error);
    }
  }, [activeCompany, activeFinancialYear, savedMem]);

  useEffect(() => {
    loadFinancialYears();
  }, [loadFinancialYears]);

  // Update dates when FY changes. Skipped on a Back restore so restored dates
  // are preserved (savedMem present); runs normally on a fresh visit.
  useEffect(() => {
    if (savedMem) return;
    const fy = financialYears.find(f => f.id === selectedFYId);
    if (fy) {
      if (fy.startDate) setDateFrom(fy.startDate);
      if (fy.endDate) setDateTo(fy.endDate);
    }
  }, [selectedFYId, financialYears, savedMem]);

  // ============================================================
  // GENERATE REPORT
  // ============================================================
  const generateReport = async () => {
    if (!activeCompany?.id || !selectedFYId) {
      alert('Please select a financial year');
      return;
    }

    setLoading(true);
    setHasGenerated(true);

    try {
      const transRef = collection(db, 'companies', activeCompany.id, 'transactions');
      // ACC-02 fix (2026-09-30): an invoice moves Posted → Partial → Paid as it
      // is settled. Filtering on 'Posted' only dropped every paid invoice from
      // the tax figures. All three are live, posted documents for tax purposes.
      const conditions = [
        where('status', 'in', ['Posted', 'Partial', 'Paid']),
        where('financialYearId', '==', selectedFYId)
      ];

      const q = query(transRef, ...conditions);
      const snapshot = await getDocs(q);
      let allTrans = snapshot.docs.map(d => ({ id: d.id, ...d.data() })).filter(t => {
        if (!activeBranch || activeBranch.id === 'all') return true;
        return t.branchId === activeBranch.id;
      });

      // Filter by date range
      if (dateFrom) {
        allTrans = allTrans.filter(t => (t.docDate || '') >= dateFrom);
      }
      if (dateTo) {
        allTrans = allTrans.filter(t => (t.docDate || '') <= dateTo);
      }

      // Filter relevant transaction types for tax (ACC-02: returns + imports added)
      // GST Output: SI, PS (+)  ·  SR (−)
      // GST Input : PI, IPI import GST (+)  ·  PR (−)
      // WHT we deducted (payable): PI, CP, BP (+)  ·  PR (−)
      // WHT deducted by customers (receivable): SI (deduct mode), CR, BR
      const GST_TYPES = ['SI', 'PS', 'SR', 'PI', 'IPI', 'PR'];
      const WHT_TYPES = ['PI', 'PR', 'CP', 'BP', 'SI', 'CR', 'BR'];
      const taxRelevant = allTrans.filter(t => {
        const tc = t.tranTypeCode || '';
        if (taxType === 'GST') return GST_TYPES.includes(tc);
        if (taxType === 'WHT') return WHT_TYPES.includes(tc);
        // All: include all GST and WHT relevant types
        return GST_TYPES.includes(tc) || WHT_TYPES.includes(tc);
      });

      setTransactions(taxRelevant);

      // Remember this generated view so Back restores it with 0 re-reads.
      saveMemory({
        selectedFYId, dateFrom, dateTo, taxType, reportType, period,
        transactions: taxRelevant,
        hasGenerated: true
      });
    } catch (error) {
      console.error('Error generating tax report:', error);
    } finally {
      setLoading(false);
    }
  };

  // ============================================================
  // COMPUTE TAX DATA
  // ============================================================
  const taxData = useMemo(() => {
    if (!transactions.length) {
      return {
        gstOutput: 0,
        gstInput: 0,
        netGST: 0,
        totalWHT: 0,
        customerWHT: 0,
        periodData: [],
        whtByVendor: [],
        detailRows: [],
        summaryRows: []
      };
    }

    let gstOutput = 0;
    let gstInput = 0;
    let totalWHT = 0;
    let customerWHT = 0;

    // Period aggregation maps
    const periodMap = {};
    // WHT by vendor
    const whtVendorMap = {};
    // Detail rows
    const detailRows = [];

    // Import GST on an IPI is a non-capitalised landed charge claimed to the
    // GST Input account (21202…); the IPI header itself carries taxAmount 0.
    const importGST = (t) => (Array.isArray(t.landedCharges) ? t.landedCharges : [])
      .filter(ch => ch && !ch.capitalize && String(ch.claimAccountCode || '').startsWith('21202'))
      .reduce((sum, ch) => sum + (Number(ch.chargeAmount) || 0), 0);

    transactions.forEach(t => {
      const tc = t.tranTypeCode || '';
      const gst = tc === 'IPI' ? importGST(t) : (Number(t.totalGST ?? t.taxAmount) || 0);
      const wht = Number(t.totalWHT ?? t.whtAmount) || 0;
      const taxable = (Number(t.subtotal) || 0) - (Number(t.discountAmount) || 0);
      const docDate = t.docDate || '';
      const periodKey = period === 'Monthly' ? getMonthKey(docDate) : getQuarterKey(docDate);

      // Initialize period bucket
      if (!periodMap[periodKey]) {
        periodMap[periodKey] = {
          key: periodKey,
          label: period === 'Monthly' ? getMonthDisplayLabel(periodKey) : getQuarterDisplayLabel(periodKey),
          salesAmount: 0,
          gstOutput: 0,
          purchaseAmount: 0,
          gstInput: 0,
          netGST: 0,
          whtDeducted: 0
        };
      }

      // Sale returns reduce output GST; purchase returns reduce input GST and
      // reverse the WHT that was deducted on the original bill.
      const sign = (tc === 'SR' || tc === 'PR') ? -1 : 1;

      // GST Output (Sales Invoices & POS Sales, less Sale Returns)
      if ((tc === 'SI' || tc === 'PS' || tc === 'SR') && gst > 0) {
        gstOutput += sign * gst;
        periodMap[periodKey].gstOutput += sign * gst;
        periodMap[periodKey].salesAmount += sign * taxable;
      }

      // GST Input (Purchase + Import Invoices, less Purchase Returns)
      if ((tc === 'PI' || tc === 'IPI' || tc === 'PR') && gst > 0) {
        gstInput += sign * gst;
        periodMap[periodKey].gstInput += sign * gst;
        periodMap[periodKey].purchaseAmount += sign * taxable;
      }

      // WHT deducted by US (Purchase Invoices, Cash/Bank Payments, less PR)
      if (['PI', 'CP', 'BP', 'PR'].includes(tc) && wht > 0) {
        const signedWht = sign * wht;
        totalWHT += signedWht;
        periodMap[periodKey].whtDeducted += signedWht;

        // Track by vendor — multi-party payments carry one line per vendor
        const vendorLines = (t.receiptMode === 'multi-party' || t.paymentMode === 'multi-party') && Array.isArray(t.partyLines) && t.partyLines.length
          ? t.partyLines.filter(l => Number(l.whtAmount) > 0).map(l => ({ key: l.partyId || l.partyName, name: l.partyName, amt: Number(l.whtAmount) || 0 }))
          : [{ key: t.partyId || t.partyName, name: t.partyName, amt: wht }];
        vendorLines.forEach(v => {
          const vendorKey = v.key || 'Unknown';
          if (!whtVendorMap[vendorKey]) {
            whtVendorMap[vendorKey] = { name: v.name || 'Unknown', value: 0, count: 0 };
          }
          whtVendorMap[vendorKey].value += sign * v.amt;
          whtVendorMap[vendorKey].count += 1;
        });
      }

      // WHT deducted from US by customers (claimable). On a sale invoice this
      // only applies in the default "deduct" mode — in 236G/H "add to total"
      // mode the WHT is collected by us and payable, so it is not a customer
      // deduction.
      if (((tc === 'SI' && t.whtAddToTotal !== true) || tc === 'CR' || tc === 'BR') && wht > 0) {
        customerWHT += wht;
      }

      // Receipts are listed only for the customer-WHT figure
      if ((tc === 'CR' || tc === 'BR') && !(wht > 0)) return;

      // Build detail row
      detailRows.push({
        id: t.id,
        docDate: docDate,
        docNumber: t.docNumber || '-',
        tranTypeCode: tc,
        partyName: t.partyName || '-',
        taxableAmount: sign * taxable,
        gst: sign * gst,
        wht: sign * wht,
        totalAmount: sign * (Number(t.totalAmount) || 0)
      });
    });

    const netGST = gstOutput - gstInput;

    // Build sorted period data
    const periodData = Object.values(periodMap)
      .sort((a, b) => a.key.localeCompare(b.key))
      .map(p => ({ ...p, netGST: p.gstOutput - p.gstInput }));

    // WHT by vendor - top 10
    const whtByVendor = Object.values(whtVendorMap)
      .sort((a, b) => b.value - a.value)
      .slice(0, 10);

    // Summary rows = period data
    const summaryRows = periodData;

    // Sort detail rows by date
    detailRows.sort((a, b) => (a.docDate || '').localeCompare(b.docDate || ''));

    return {
      gstOutput,
      gstInput,
      netGST,
      totalWHT,
      customerWHT,
      periodData,
      whtByVendor,
      detailRows,
      summaryRows
    };
  }, [transactions, period]);

  // ============================================================
  // CHART DATA
  // ============================================================
  const gstBarChartData = useMemo(() => {
    if (!hasGenerated || !taxData.periodData.length) return [];
    return taxData.periodData.map(p => ({
      name: p.label,
      [`${taxLabel} Output`]: p.gstOutput,
      [`${taxLabel} Input`]: p.gstInput
    }));
  }, [hasGenerated, taxData.periodData]);

  const netGSTLineData = useMemo(() => {
    if (!hasGenerated || !taxData.periodData.length) return [];
    return taxData.periodData.map(p => ({
      name: p.label,
      [`Net ${taxLabel} Payable`]: p.netGST
    }));
  }, [hasGenerated, taxData.periodData]);

  const whtPieData = useMemo(() => {
    if (!hasGenerated || !taxData.whtByVendor.length) return [];
    return taxData.whtByVendor;
  }, [hasGenerated, taxData.whtByVendor]);

  // ============================================================
  // CUSTOM TOOLTIPS
  // ============================================================
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <TooltipBox>
          <TooltipLabel>{label}</TooltipLabel>
          {payload.map((entry, i) => (
            <TooltipRow key={i}>
              <TooltipDot style={{ background: entry.color }} />
              <span>{entry.name}: {formatCurrency(entry.value)}</span>
            </TooltipRow>
          ))}
        </TooltipBox>
      );
    }
    return null;
  };

  const PieTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0];
      const total = whtPieData.reduce((s, d) => s + d.value, 0);
      const pct = total > 0 ? ((data.value / total) * 100).toFixed(1) : '0.0';
      return (
        <TooltipBox>
          <TooltipLabel>{data.name}</TooltipLabel>
          <TooltipRow>
            <span>{formatCurrency(data.value)} ({pct}%)</span>
          </TooltipRow>
          <TooltipRow>
            <span style={{ fontSize: 11, color: colors.text.tertiary }}>
              {data.payload.count} transaction{data.payload.count !== 1 ? 's' : ''}
            </span>
          </TooltipRow>
        </TooltipBox>
      );
    }
    return null;
  };

  // ============================================================
  // CSV EXPORT
  // ============================================================
  const handleExport = () => {
    if (!hasGenerated) return;
    const selectedFY = financialYears.find(f => f.id === selectedFYId);

    let csv = `Tax Report - ${taxLabel} & WHT Summary\n`;
    csv += `Company: ${activeCompany?.companyName || ''}\n`;
    csv += `Financial Year: ${selectedFY?.yearName || selectedFY?.yearCode || ''}\n`;
    csv += `Period: ${fmtDate(dateFrom)} to ${fmtDate(dateTo)}\n`;
    csv += `Tax Type: ${taxType}\n`;
    csv += `Report Type: ${reportType}\n`;
    csv += `Grouping: ${period}\n\n`;

    // Summary totals
    csv += 'Summary\n';
    csv += `${taxLabel} Output (Collected on Sales),${fmtAmount(taxData.gstOutput)}\n`;
    csv += `${taxLabel} Input (Paid on Purchases),${fmtAmount(taxData.gstInput)}\n`;
    csv += `Net ${taxLabel} Payable,${fmtAmount(taxData.netGST)}\n`;
    csv += `Total WHT Deducted,${fmtAmount(taxData.totalWHT)}\n`;
    csv += `WHT Deducted by Customers,${fmtAmount(taxData.customerWHT)}\n\n`;

    if (reportType === 'Summary') {
      csv += `Period,Sales Amount,${taxLabel} Output,Purchase Amount,${taxLabel} Input,Net ${taxLabel},WHT Deducted\n`;
      taxData.summaryRows.forEach(row => {
        csv += `"${row.label}",${fmtAmount(row.salesAmount)},${fmtAmount(row.gstOutput)},${fmtAmount(row.purchaseAmount)},${fmtAmount(row.gstInput)},${fmtAmount(row.netGST)},${fmtAmount(row.whtDeducted)}\n`;
      });
    } else {
      csv += `Date,Doc #,Type,Party,Taxable Amount,${taxLabel},WHT,Total\n`;
      taxData.detailRows.forEach(row => {
        csv += `${fmtDate(row.docDate)},"${row.docNumber}","${row.tranTypeCode}","${row.partyName}",${fmtAmount(row.taxableAmount)},${fmtAmount(row.gst)},${fmtAmount(row.wht)},${fmtAmount(row.totalAmount)}\n`;
      });
    }

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Tax_Report_${taxType}_${dateFrom}_${dateTo}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // ============================================================
  // PRINT
  // ============================================================
  const handlePrint = () => {
    if (!hasGenerated) return;
    const selectedFY = financialYears.find(f => f.id === selectedFYId);

    let tableHeaders = '';
    let tableRows = '';

    if (reportType === 'Summary') {
      tableHeaders = `
        <th>Period</th>
        <th class="amount">Sales Amount</th>
        <th class="amount">${taxLabel} Output</th>
        <th class="amount">Purchase Amount</th>
        <th class="amount">${taxLabel} Input</th>
        <th class="amount">Net ${taxLabel}</th>
        <th class="amount">WHT Deducted</th>
      `;

      let totSales = 0, totGSTOut = 0, totPurch = 0, totGSTIn = 0, totNetGST = 0, totWHT = 0;
      taxData.summaryRows.forEach(row => {
        totSales += row.salesAmount;
        totGSTOut += row.gstOutput;
        totPurch += row.purchaseAmount;
        totGSTIn += row.gstInput;
        totNetGST += row.netGST;
        totWHT += row.whtDeducted;
        tableRows += `<tr>
          <td>${row.label}</td>
          <td class="amount">${fmtAmount(row.salesAmount)}</td>
          <td class="amount">${fmtAmount(row.gstOutput)}</td>
          <td class="amount">${fmtAmount(row.purchaseAmount)}</td>
          <td class="amount">${fmtAmount(row.gstInput)}</td>
          <td class="amount ${row.netGST < 0 ? 'negative' : ''}">${fmtAmount(row.netGST)}</td>
          <td class="amount">${fmtAmount(row.whtDeducted)}</td>
        </tr>`;
      });

      tableRows += `<tr class="total-row">
        <td><strong>Total</strong></td>
        <td class="amount"><strong>${fmtAmount(totSales)}</strong></td>
        <td class="amount"><strong>${fmtAmount(totGSTOut)}</strong></td>
        <td class="amount"><strong>${fmtAmount(totPurch)}</strong></td>
        <td class="amount"><strong>${fmtAmount(totGSTIn)}</strong></td>
        <td class="amount ${totNetGST < 0 ? 'negative' : ''}"><strong>${fmtAmount(totNetGST)}</strong></td>
        <td class="amount"><strong>${fmtAmount(totWHT)}</strong></td>
      </tr>`;
    } else {
      tableHeaders = `
        <th>Date</th>
        <th>Doc #</th>
        <th>Type</th>
        <th>Party</th>
        <th class="amount">Taxable Amount</th>
        <th class="amount">${taxLabel}</th>
        <th class="amount">WHT</th>
        <th class="amount">Total</th>
      `;

      let totTaxable = 0, totGST = 0, totWHT = 0, totTotal = 0;
      taxData.detailRows.forEach(row => {
        totTaxable += row.taxableAmount;
        totGST += row.gst;
        totWHT += row.wht;
        totTotal += row.totalAmount;
        tableRows += `<tr>
          <td>${fmtDate(row.docDate)}</td>
          <td>${row.docNumber}</td>
          <td><span class="badge badge-${row.tranTypeCode}">${row.tranTypeCode}</span></td>
          <td>${row.partyName}</td>
          <td class="amount">${fmtAmount(row.taxableAmount)}</td>
          <td class="amount">${fmtAmount(row.gst)}</td>
          <td class="amount">${fmtAmount(row.wht)}</td>
          <td class="amount">${fmtAmount(row.totalAmount)}</td>
        </tr>`;
      });

      tableRows += `<tr class="total-row">
        <td colspan="4"><strong>Total</strong></td>
        <td class="amount"><strong>${fmtAmount(totTaxable)}</strong></td>
        <td class="amount"><strong>${fmtAmount(totGST)}</strong></td>
        <td class="amount"><strong>${fmtAmount(totWHT)}</strong></td>
        <td class="amount"><strong>${fmtAmount(totTotal)}</strong></td>
      </tr>`;
    }

    const w = window.open('', '_blank');
    w.document.write(`<html><head><title>Tax Report - ${taxLabel} & WHT Summary</title>
      <style>
        body{font-family:'Segoe UI',sans-serif;margin:30px;color:#1A1A1A;font-size:13px}
        .header{background:#800020;color:white;padding:20px;border-radius:8px;margin-bottom:20px}
        .header h1{margin:0;font-size:22px}
        .header .meta{font-size:12px;opacity:0.9;margin-top:6px}
        .summary-cards{display:flex;gap:16px;margin-bottom:20px;flex-wrap:wrap}
        .summary-card{flex:1;min-width:180px;padding:16px;border:1px solid #e0e0e0;border-radius:8px;text-align:center}
        .summary-card .label{font-size:11px;color:#666;text-transform:uppercase;margin-bottom:4px}
        .summary-card .value{font-size:20px;font-weight:700}
        .summary-card .value.green{color:#28A745}
        .summary-card .value.red{color:#DC3545}
        .summary-card .value.maroon{color:#800020}
        table{width:100%;border-collapse:collapse;margin-bottom:15px}
        th{background:#f5f5f5;padding:10px;text-align:left;font-size:11px;text-transform:uppercase;border-bottom:2px solid #800020}
        th.amount{text-align:right}
        td{padding:8px 10px;border-bottom:1px solid #eee}
        td.amount{text-align:right;font-family:'Consolas',monospace;font-size:12px}
        .total-row td{font-weight:700;border-top:2px solid #800020;background:#f9f5f6}
        .negative{color:#DC3545}
        .badge{padding:2px 8px;border-radius:4px;font-size:11px;font-weight:600}
        .badge-SI{background:#E8F5E9;color:#2E7D32}
        .badge-PS{background:#E8F5E9;color:#2E7D32}
        .badge-PI{background:#FFF3E0;color:#E65100}
        .badge-CP,.badge-BP{background:#E3F2FD;color:#1565C0}
        @media print{body{margin:15px}.header{-webkit-print-color-adjust:exact;print-color-adjust:exact}}
      </style></head><body>
      <div class="header">
        <h1>Tax Report - ${taxLabel} & WHT Summary</h1>
        <div class="meta">
          ${activeCompany?.companyName || 'Company'} |
          Financial Year: ${selectedFY?.yearName || selectedFY?.yearCode || ''} |
          Period: ${fmtDate(dateFrom)} to ${fmtDate(dateTo)} |
          Tax Type: ${taxType} | ${reportType} View | ${period}
        </div>
      </div>
      <div class="summary-cards">
        <div class="summary-card">
          <div class="label">${taxLabel} Output</div>
          <div class="value green">${fmtAmount(taxData.gstOutput)}</div>
        </div>
        <div class="summary-card">
          <div class="label">${taxLabel} Input</div>
          <div class="value red">${fmtAmount(taxData.gstInput)}</div>
        </div>
        <div class="summary-card">
          <div class="label">Net ${taxLabel} Payable</div>
          <div class="value maroon">${fmtAmount(taxData.netGST)}</div>
        </div>
        <div class="summary-card">
          <div class="label">Total WHT</div>
          <div class="value maroon">${fmtAmount(taxData.totalWHT)}</div>
        </div>
      </div>
      <table>
        <thead><tr>${tableHeaders}</tr></thead>
        <tbody>${tableRows}</tbody>
      </table>
    </body></html>`);
    addPrintBrand(w);
    w.document.close();
    w.print();
  };

  // ============================================================
  // DETERMINE VISIBILITY BASED ON TAX TYPE FILTER
  // ============================================================
  const showGST = taxType === 'All' || taxType === 'GST';
  const showWHT = taxType === 'All' || taxType === 'WHT';

  // ============================================================
  // CHECK DATA PRESENCE
  // ============================================================
  const hasData = transactions.length > 0;
  const hasGSTData = taxData.gstOutput > 0 || taxData.gstInput > 0;
  const hasWHTData = taxData.totalWHT > 0 || taxData.customerWHT > 0;
  const selectedFY = financialYears.find(f => f.id === selectedFYId);

  // ============================================================
  // RENDER
  // ============================================================
  return (
    <PageLayout pageTitle="Tax Reports" breadcrumb="Reports / Accounting Reports / Tax Reports">
      <ViewTabs role="tablist">
        <ViewTab type="button" role="tab" aria-selected={view === 'summary'} $active={view === 'summary'} onClick={() => setView('summary')}>{taxLabel} &amp; WHT Summary</ViewTab>
        <ViewTab type="button" role="tab" aria-selected={view === 'wht'} $active={view === 'wht'} onClick={() => setView('wht')}>WHT Statement</ViewTab>
      </ViewTabs>
      {view === 'wht' ? <WhtStatement /> : (<>
      {/* Summary Cards */}
      {hasGenerated && !loading && hasData && (
        <SummaryCards>
          {showGST && (
            <SummaryCard>
              <SummaryIcon $color="#28A745"><FiTrendingUp /></SummaryIcon>
              <SummaryInfo>
                <SummaryLabel>{taxLabel} Output</SummaryLabel>
                <SummarySubLabel>Collected on Sales</SummarySubLabel>
                <SummaryValue $color="#28A745">
                  {formatCurrency(taxData.gstOutput)}
                </SummaryValue>
              </SummaryInfo>
            </SummaryCard>
          )}
          {showGST && (
            <SummaryCard>
              <SummaryIcon $color="#DC3545"><FiTrendingDown /></SummaryIcon>
              <SummaryInfo>
                <SummaryLabel>{taxLabel} Input</SummaryLabel>
                <SummarySubLabel>Paid on Purchases</SummarySubLabel>
                <SummaryValue $color="#DC3545">
                  {formatCurrency(taxData.gstInput)}
                </SummaryValue>
              </SummaryInfo>
            </SummaryCard>
          )}
          {showGST && (
            <SummaryCard>
              <SummaryIcon $color={taxData.netGST >= 0 ? colors.primary.main : '#DC3545'}>
                <FiDollarSign />
              </SummaryIcon>
              <SummaryInfo>
                <SummaryLabel>Net {taxLabel} Payable</SummaryLabel>
                <SummarySubLabel>Output - Input</SummarySubLabel>
                <SummaryValue $color={taxData.netGST >= 0 ? colors.primary.main : '#DC3545'}>
                  {taxData.netGST < 0 ? '(' : ''}{formatCurrency(Math.abs(taxData.netGST))}{taxData.netGST < 0 ? ')' : ''}
                </SummaryValue>
              </SummaryInfo>
            </SummaryCard>
          )}
          {showWHT && (
            <SummaryCard>
              <SummaryIcon $color={CHART_COLORS.accent3}><FiPercent /></SummaryIcon>
              <SummaryInfo>
                <SummaryLabel>Total WHT Deducted</SummaryLabel>
                <SummarySubLabel>Withholding Tax</SummarySubLabel>
                <SummaryValue $color={CHART_COLORS.accent3}>
                  {formatCurrency(taxData.totalWHT)}
                </SummaryValue>
              </SummaryInfo>
            </SummaryCard>
          )}
          {showWHT && taxData.customerWHT > 0 && (
            <SummaryCard>
              <SummaryIcon $color={CHART_COLORS.accent2}><FiPercent /></SummaryIcon>
              <SummaryInfo>
                <SummaryLabel>WHT Deducted by Customers</SummaryLabel>
                <SummarySubLabel>Claimable (on sales / receipts)</SummarySubLabel>
                <SummaryValue $color={CHART_COLORS.accent2}>
                  {formatCurrency(taxData.customerWHT)}
                </SummaryValue>
              </SummaryInfo>
            </SummaryCard>
          )}
        </SummaryCards>
      )}

      {/* Filters */}
      <FiltersCard>
        <FiltersRow>
          <FilterGroup>
            <Label>Financial Year</Label>
            <Select value={selectedFYId} onChange={e => setSelectedFYId(e.target.value)}>
              <option value="">Select Financial Year</option>
              {financialYears.map(fy => (
                <option key={fy.id} value={fy.id}>
                  {fy.yearName || fy.yearCode}
                  {fy.isActive ? ' (Active)' : ''}
                </option>
              ))}
            </Select>
          </FilterGroup>
          <FilterGroup>
            <Label>From Date</Label>
            <DateInput type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
          </FilterGroup>
          <FilterGroup>
            <Label>To Date</Label>
            <DateInput type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} />
          </FilterGroup>
          <FilterGroup>
            <Label>Tax Type</Label>
            <Select value={taxType} onChange={e => setTaxType(e.target.value)}>
              <option value="All">All</option>
              <option value="GST">{taxLabel} Only</option>
              <option value="WHT">WHT Only</option>
            </Select>
          </FilterGroup>
          <FilterGroup>
            <Label>Report Type</Label>
            <Select value={reportType} onChange={e => setReportType(e.target.value)}>
              <option value="Summary">Summary</option>
              <option value="Detail">Detail</option>
            </Select>
          </FilterGroup>
          <FilterGroup>
            <Label>Period</Label>
            <Select value={period} onChange={e => setPeriod(e.target.value)}>
              <option value="Monthly">Monthly</option>
              <option value="Quarterly">Quarterly</option>
            </Select>
          </FilterGroup>
        </FiltersRow>
        <FiltersRow style={{ marginTop: 14 }}>
          <ButtonGroup>
            <Button onClick={handleExport} disabled={!hasGenerated || loading || !hasData}>
              <FiDownload /> CSV Export
            </Button>
            <Button onClick={handlePrint} disabled={!hasGenerated || loading || !hasData}>
              <FiPrinter /> Print
            </Button>
            <PrimaryButton onClick={generateReport} disabled={loading}>
              <FiBarChart2 /> {loading ? 'Generating...' : 'Generate Report'}
            </PrimaryButton>
          </ButtonGroup>
        </FiltersRow>
      </FiltersCard>

      {/* Data Table */}
      <ContentCard>
        {hasGenerated && !loading && hasData && (
          <ReportHeader>
            <ReportTitle>
              {reportType === 'Summary' ? 'Tax Summary' : 'Tax Detail'} - {taxType === 'All' ? `${taxLabel} & WHT` : taxType}
            </ReportTitle>
            <ReportMeta>
              {activeCompany?.companyName || ''} | {selectedFY?.yearName || selectedFY?.yearCode || ''} | {fmtDate(dateFrom)} to {fmtDate(dateTo)} | {period}
            </ReportMeta>
          </ReportHeader>
        )}

        <TableContainer>
          {loading ? (
            <EmptyState>
              <LoadingSpinner />
              <div style={{ marginTop: 16 }}>Generating Tax Report...</div>
            </EmptyState>
          ) : !hasGenerated ? (
            <EmptyState>
              <FiCalendar size={40} style={{ opacity: 0.3, marginBottom: 12 }} />
              <div>Select a financial year and date range, then click Generate Report</div>
            </EmptyState>
          ) : !hasData ? (
            <EmptyState>
              <FiFileText size={40} style={{ opacity: 0.3, marginBottom: 12 }} />
              <div>No tax transactions found for the selected period</div>
              <SubText>Ensure transactions are posted and have {taxLabel}/WHT amounts recorded.</SubText>
            </EmptyState>
          ) : reportType === 'Summary' ? (
            /* SUMMARY TABLE */
            <Table>
              <thead>
                <Tr>
                  <Th>Period</Th>
                  {showGST && <Th $right>Sales Amount</Th>}
                  {showGST && <Th $right>{taxLabel} Output</Th>}
                  {showGST && <Th $right>Purchase Amount</Th>}
                  {showGST && <Th $right>{taxLabel} Input</Th>}
                  {showGST && <Th $right>Net {taxLabel}</Th>}
                  {showWHT && <Th $right>WHT Deducted</Th>}
                </Tr>
              </thead>
              <tbody>
                {taxData.summaryRows.map((row, idx) => (
                  <DetailRow key={idx}>
                    <NameTd $bold>{row.label}</NameTd>
                    {showGST && <AmountTd>{formatCurrency(row.salesAmount)}</AmountTd>}
                    {showGST && <AmountTd>{formatCurrency(row.gstOutput)}</AmountTd>}
                    {showGST && <AmountTd>{formatCurrency(row.purchaseAmount)}</AmountTd>}
                    {showGST && <AmountTd>{formatCurrency(row.gstInput)}</AmountTd>}
                    {showGST && (
                      <AmountTd $negative={row.netGST < 0} $bold>
                        {row.netGST < 0 ? '(' : ''}{formatCurrency(Math.abs(row.netGST))}{row.netGST < 0 ? ')' : ''}
                      </AmountTd>
                    )}
                    {showWHT && <AmountTd>{formatCurrency(row.whtDeducted)}</AmountTd>}
                  </DetailRow>
                ))}

                {/* Totals Row */}
                <TotalRow>
                  <NameTd $bold>Total</NameTd>
                  {showGST && (
                    <AmountTd $bold>
                      {formatCurrency(taxData.summaryRows.reduce((s, r) => s + r.salesAmount, 0))}
                    </AmountTd>
                  )}
                  {showGST && (
                    <AmountTd $bold>
                      {formatCurrency(taxData.gstOutput)}
                    </AmountTd>
                  )}
                  {showGST && (
                    <AmountTd $bold>
                      {formatCurrency(taxData.summaryRows.reduce((s, r) => s + r.purchaseAmount, 0))}
                    </AmountTd>
                  )}
                  {showGST && (
                    <AmountTd $bold>
                      {formatCurrency(taxData.gstInput)}
                    </AmountTd>
                  )}
                  {showGST && (
                    <AmountTd $bold $negative={taxData.netGST < 0}>
                      {taxData.netGST < 0 ? '(' : ''}{formatCurrency(Math.abs(taxData.netGST))}{taxData.netGST < 0 ? ')' : ''}
                    </AmountTd>
                  )}
                  {showWHT && (
                    <AmountTd $bold>
                      {formatCurrency(taxData.totalWHT)}
                    </AmountTd>
                  )}
                </TotalRow>
              </tbody>
            </Table>
          ) : (
            /* DETAIL TABLE */
            <Table>
              <thead>
                <Tr>
                  <Th>Date</Th>
                  <Th>Doc #</Th>
                  <Th>Type</Th>
                  <Th>Party</Th>
                  <Th $right>Taxable Amount</Th>
                  {showGST && <Th $right>{taxLabel}</Th>}
                  {showWHT && <Th $right>WHT</Th>}
                  <Th $right>Total</Th>
                </Tr>
              </thead>
              <tbody>
                {taxData.detailRows.map((row, idx) => (
                  <DetailRow key={row.id || idx}>
                    <DateTd>{formatDate(row.docDate)}</DateTd>
                    <CodeTd
                      style={{ cursor: 'pointer', color: '#1a73e8' }}
                      onClick={() => navigateToTransaction(navigate, row.tranTypeCode, row.id)}
                      onMouseEnter={e => e.currentTarget.style.textDecoration = 'underline'}
                      onMouseLeave={e => e.currentTarget.style.textDecoration = 'none'}
                    >
                      {row.docNumber} <FiExternalLink size={12} style={{ marginLeft: 4, verticalAlign: 'middle' }} />
                    </CodeTd>
                    <NameTd>
                      <TypeBadge $type={row.tranTypeCode}>{row.tranTypeCode}</TypeBadge>
                    </NameTd>
                    <NameTd>{row.partyName}</NameTd>
                    <AmountTd>{formatCurrency(row.taxableAmount)}</AmountTd>
                    {showGST && (
                      <AmountTd $highlight={row.gst > 0}>
                        {row.gst > 0 ? formatCurrency(row.gst) : '-'}
                      </AmountTd>
                    )}
                    {showWHT && (
                      <AmountTd $highlight={row.wht > 0}>
                        {row.wht > 0 ? formatCurrency(row.wht) : '-'}
                      </AmountTd>
                    )}
                    <AmountTd $bold>{formatCurrency(row.totalAmount)}</AmountTd>
                  </DetailRow>
                ))}

                {/* Totals Row */}
                <TotalRow>
                  <NameTd $bold colSpan={4}>Total ({taxData.detailRows.length} transactions)</NameTd>
                  <AmountTd $bold>
                    {formatCurrency(taxData.detailRows.reduce((s, r) => s + r.taxableAmount, 0))}
                  </AmountTd>
                  {showGST && (
                    <AmountTd $bold>
                      {formatCurrency(taxData.detailRows.reduce((s, r) => s + r.gst, 0))}
                    </AmountTd>
                  )}
                  {showWHT && (
                    <AmountTd $bold>
                      {formatCurrency(taxData.detailRows.reduce((s, r) => s + r.wht, 0))}
                    </AmountTd>
                  )}
                  <AmountTd $bold>
                    {formatCurrency(taxData.detailRows.reduce((s, r) => s + r.totalAmount, 0))}
                  </AmountTd>
                </TotalRow>
              </tbody>
            </Table>
          )}
        </TableContainer>
      </ContentCard>

      {/* Charts */}
      {hasGenerated && !loading && hasData && (
        <ChartsGrid>
          {/* GST Output vs Input - Grouped Bar Chart */}
          {showGST && gstBarChartData.length > 0 && (
            <ChartCard>
              <ChartTitle>{taxLabel} Output vs Input by {period === 'Monthly' ? 'Month' : 'Quarter'}</ChartTitle>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={gstBarChartData} margin={{ top: 10, right: 30, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} angle={-30} textAnchor="end" height={60} />
                  <YAxis tick={{ fontSize: 12 }} tickFormatter={v => formatNumber(v, { compact: true })} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey={`${taxLabel} Output`} fill={CHART_COLORS.primary} radius={[4, 4, 0, 0]} />
                  <Bar dataKey={`${taxLabel} Input`} fill={CHART_COLORS.accent1} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          )}

          {/* Net GST Payable Trend - Line Chart */}
          {showGST && netGSTLineData.length > 0 && (
            <ChartCard>
              <ChartTitle>Net {taxLabel} Payable Trend</ChartTitle>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={netGSTLineData} margin={{ top: 10, right: 30, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} angle={-30} textAnchor="end" height={60} />
                  <YAxis tick={{ fontSize: 12 }} tickFormatter={v => formatNumber(v, { compact: true })} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Line
                    type="monotone"
                    dataKey={`Net ${taxLabel} Payable`}
                    stroke={CHART_COLORS.secondary}
                    strokeWidth={2}
                    dot={{ fill: CHART_COLORS.secondary, r: 4 }}
                    activeDot={{ r: 6 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </ChartCard>
          )}

          {/* WHT by Vendor - Pie Chart */}
          {showWHT && whtPieData.length > 0 && (
            <ChartCard $full={!showGST || (!hasGSTData && showWHT)}>
              <ChartTitle>WHT by Vendor (Top 10)</ChartTitle>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={whtPieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={55}
                    outerRadius={105}
                    paddingAngle={2}
                    dataKey="value"
                    label={({ name, percent }) => `${name.length > 15 ? name.substring(0, 15) + '...' : name} (${(percent * 100).toFixed(0)}%)`}
                    labelLine={{ stroke: '#999', strokeWidth: 1 }}
                  >
                    {whtPieData.map((_, i) => (
                      <Cell key={`cell-${i}`} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip content={<PieTooltip />} />
                </PieChart>
              </ResponsiveContainer>
            </ChartCard>
          )}
        </ChartsGrid>
      )}
      </>)}
    </PageLayout>
  );
};

// ============================================================
// STYLED COMPONENTS
// ============================================================

const ViewTabs = styled.div`
  display: inline-flex; gap: 4px; padding: 4px; margin-bottom: 16px;
  background: ${colors.neutral?.[100] || '#f1f1f1'}; border-radius: 10px;
`;

const ViewTab = styled.button`
  border: none; cursor: pointer; padding: 8px 18px; border-radius: 8px; font-size: 14px; font-weight: 600;
  background: ${props => props.$active ? 'white' : 'transparent'};
  color: ${props => props.$active ? colors.primary.main : colors.text.secondary};
  box-shadow: ${props => props.$active ? '0 1px 3px rgba(0,0,0,0.12)' : 'none'};
`;

const SummaryCards = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 16px;
  margin-bottom: 20px;
`;

const SummaryCard = styled.div`
  background: white;
  border-radius: ${colors.borderRadius.lg};
  box-shadow: ${colors.shadows.sm};
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 16px;
`;

const SummaryIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: ${colors.borderRadius.md};
  background: ${props => props.$color || colors.primary.main}15;
  color: ${props => props.$color || colors.primary.main};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
  flex-shrink: 0;
`;

const SummaryInfo = styled.div`
  min-width: 0;
`;

const SummaryLabel = styled.div`
  font-size: 12px;
  color: ${colors.text.secondary};
  font-weight: 600;
`;

const SummarySubLabel = styled.div`
  font-size: 10px;
  color: ${colors.text.tertiary};
  margin-bottom: 4px;
`;

const SummaryValue = styled.div`
  font-size: 20px;
  font-weight: 600;
  color: ${props => props.$color || colors.text.primary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const FiltersCard = styled.div`
  background: white;
  border-radius: ${colors.borderRadius.lg};
  box-shadow: ${colors.shadows.sm};
  padding: 20px;
  margin-bottom: 20px;
`;

const FiltersRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  align-items: flex-end;
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 140px;
`;

const Label = styled.label`
  font-size: 11px;
  font-weight: 600;
  color: ${colors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.3px;
`;

const Select = styled.select`
  padding: 9px 12px;
  border: 1px solid ${colors.neutral[300]};
  border-radius: ${colors.borderRadius.md};
  font-size: 13px;
  color: ${colors.text.primary};
  background: white;
  cursor: pointer;
  &:focus { outline: none; border-color: ${colors.primary.main}; }
`;

const DateInput = styled.input`
  padding: 9px 12px;
  border: 1px solid ${colors.neutral[300]};
  border-radius: ${colors.borderRadius.md};
  font-size: 13px;
  color: ${colors.text.primary};
  &:focus { outline: none; border-color: ${colors.primary.main}; }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 8px;
  margin-left: auto;
`;

const Button = styled.button`
  padding: 9px 14px;
  border: 1px solid ${colors.neutral[300]};
  border-radius: ${colors.borderRadius.md};
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  background: white;
  color: ${colors.text.primary};
  transition: all 0.2s;
  &:hover { background: ${colors.neutral[100]}; border-color: ${colors.primary.main}; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const PrimaryButton = styled.button`
  padding: 9px 18px;
  border: none;
  border-radius: ${colors.borderRadius.md};
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  background: ${colors.primary.main};
  color: white;
  transition: all 0.2s;
  &:hover { background: ${colors.primary.dark}; }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

// Charts
const ChartsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
  margin-bottom: 20px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const ChartCard = styled.div`
  background: white;
  border-radius: ${colors.borderRadius.lg};
  box-shadow: ${colors.shadows.sm};
  padding: 20px;
  ${props => props.$full && 'grid-column: 1 / -1;'}
`;

const ChartTitle = styled.h4`
  margin: 0 0 16px 0;
  font-size: 14px;
  font-weight: 600;
  color: ${colors.text.primary};
`;

// Tooltip
const TooltipBox = styled.div`
  background: white;
  border: 1px solid ${colors.neutral[200]};
  border-radius: ${colors.borderRadius.md};
  box-shadow: ${colors.shadows.md};
  padding: 12px;
  font-size: 12px;
`;

const TooltipLabel = styled.div`
  font-weight: 600;
  margin-bottom: 6px;
  color: ${colors.text.primary};
`;

const TooltipRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  color: ${colors.text.secondary};
  margin-bottom: 2px;
`;

const TooltipDot = styled.div`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
`;

// Content / Table
const ContentCard = styled.div`
  background: white;
  border-radius: ${colors.borderRadius.lg};
  box-shadow: ${colors.shadows.sm};
  overflow: hidden;
`;

const ReportHeader = styled.div`
  padding: 20px;
  border-bottom: 1px solid ${colors.neutral[200]};
  background: ${colors.neutral[50]};
`;

const ReportTitle = styled.h3`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: ${colors.primary.main};
`;

const ReportMeta = styled.p`
  margin: 6px 0 0;
  font-size: 13px;
  color: ${colors.text.secondary};
`;

const TableContainer = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Tr = styled.tr``;

const Th = styled.th`
  padding: 12px 16px;
  text-align: ${props => props.$right ? 'right' : 'left'};
  font-size: 11px;
  font-weight: 600;
  color: ${colors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  background: ${colors.neutral[50]};
  border-bottom: 2px solid ${colors.neutral[200]};
  white-space: nowrap;
`;

const DetailRow = styled.tr`
  border-bottom: 1px solid ${colors.neutral[100]};
  &:hover { background: ${colors.neutral[50]}; }
`;

const TotalRow = styled.tr`
  background: ${colors.primary.main}0A;
  border-top: 2px solid ${colors.primary.main};
  border-bottom: 2px solid ${colors.primary.main};
`;

const DateTd = styled.td`
  padding: 10px 16px;
  font-size: 12px;
  color: ${colors.text.secondary};
  white-space: nowrap;
`;

const CodeTd = styled.td`
  padding: 10px 16px;
  font-family: 'SF Mono', 'Consolas', 'Courier New', monospace;
  font-size: 12px;
  color: ${colors.text.primary};
  white-space: nowrap;
  font-weight: 500;
`;

const NameTd = styled.td`
  padding: 10px 16px;
  font-size: 13px;
  color: ${colors.text.primary};
  font-weight: ${props => props.$bold ? 600 : 400};
`;

const AmountTd = styled.td`
  padding: 10px 16px;
  text-align: right;
  font-family: 'SF Mono', 'Consolas', 'Courier New', monospace;
  font-size: 13px;
  font-weight: ${props => props.$bold ? 600 : 400};
  color: ${props => {
    if (props.$negative) return '#DC3545';
    if (props.$highlight) return colors.primary.main;
    return colors.text.primary;
  }};
  white-space: nowrap;
`;

const TypeBadge = styled.span`
  display: inline-block;
  padding: 3px 10px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.3px;
  background: ${props => {
    switch (props.$type) {
      case 'SI': return '#E8F5E9';
      case 'PS': return '#E8F5E9';
      case 'PI': return '#FFF3E0';
      case 'CP': return '#E3F2FD';
      case 'BP': return '#E8EAF6';
      default: return colors.neutral[100];
    }
  }};
  color: ${props => {
    switch (props.$type) {
      case 'SI': return '#2E7D32';
      case 'PS': return '#2E7D32';
      case 'PI': return '#E65100';
      case 'CP': return '#1565C0';
      case 'BP': return '#283593';
      default: return colors.text.secondary;
    }
  }};
`;

// Loading & Empty states
const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: ${colors.text.secondary};
  font-size: 14px;
`;

const SubText = styled.p`
  font-size: 13px;
  color: ${colors.text.tertiary};
  margin-top: 8px;
`;

const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 3px solid ${colors.neutral[200]};
  border-top: 3px solid ${colors.primary.main};
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  margin: 0 auto;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

export default TaxReports;
